/**
 * The document-type catalogue, as the web reads it: ONE module, one query key
 * per scope (Library LB6-T10 — decision 40 / C4, principle 3; acceptance L6-13).
 *
 * The catalogue is the API's — rows of `document_types`: the built-in types
 * (platform rows) and the tenant's own custom types. There is NO list of type
 * names in the browser: the upload dialog's picker, the document edit form's
 * picker, the type badge's label and the Manage document types dialog all
 * read THIS module, so a type added in the dialog is offered by both pickers
 * and shown by the badge with the label its row carries.
 *
 *   tenant  GET /knowledge/document-types                — built-ins + the tenant's own
 *   system  GET /admin/platform/library/document-types   — the built-ins (the System
 *                                                          Library has no tenant)
 *
 * A failed read is an ERROR, never an empty catalogue: the fetcher rejects on
 * a response without the list, and on an EMPTY list too — the built-ins are
 * always there (c45), so "no types" can only mean the read went wrong.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { DocumentTypeEntry, DocumentTypeListResponse } from '@argusai/shared';
import { apiClient } from '@/lib/api-client';

/** Whose catalogue: the tenant's, or the System Library's (built-ins only). */
export type DocumentTypeScope = 'tenant' | 'system';

/** The one tenant route (GET lists, POST creates, DELETE /:id removes). */
export const DOCUMENT_TYPE_CATALOGUE_PATH = '/knowledge/document-types';
/** The System Library's catalogue (read-only: the built-ins). */
export const SYSTEM_DOCUMENT_TYPE_CATALOGUE_PATH = '/admin/platform/library/document-types';

/**
 * One catalogue entry, as the API returns it — the wire shape declared once in
 * @argusai/shared (DocumentTypeEntrySchema, review LB6T10-R2). `name` is the
 * key a document stores.
 */
export type { DocumentTypeEntry };

export const documentTypesQueryKey = (scope: DocumentTypeScope) => ['library', 'document-types', scope] as const;

/** Shown wherever the catalogue could not be read (both pickers, the dialog). */
export const DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT = 'Document types could not be loaded.';

/** The scope's catalogue. Rejects on a failed read, a malformed answer or an empty one. */
export async function fetchDocumentTypes(scope: DocumentTypeScope): Promise<DocumentTypeEntry[]> {
  const res = await apiClient.get<DocumentTypeListResponse>(
    scope === 'system' ? SYSTEM_DOCUMENT_TYPE_CATALOGUE_PATH : DOCUMENT_TYPE_CATALOGUE_PATH,
  );
  const data = res?.data;
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT);
  }
  return data;
}

/**
 * The scope's catalogue as a query. `staleTime: 0` — a picker opened after a
 * type was added elsewhere refetches instead of showing a five-minute-old list
 * (the app default, lib/query-client.ts).
 */
export function useDocumentTypes(opts: { scope?: DocumentTypeScope; enabled?: boolean } = {}) {
  const scope = opts.scope ?? 'tenant';
  return useQuery<DocumentTypeEntry[], Error>({
    queryKey: documentTypesQueryKey(scope),
    queryFn: () => fetchDocumentTypes(scope),
    enabled: opts.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * What a document's stored key reads as: its catalogue row's label. A key the
 * catalogue does not hold (a type deleted after the document took it, or one
 * stored before c45) reads as the key itself — never a guessed label.
 */
export function documentTypeLabel(types: readonly DocumentTypeEntry[] | undefined, key: string): string {
  return types?.find((t) => t.name === key)?.label ?? key;
}

/**
 * The options a picker offers: the catalogue, plus the document's CURRENT key
 * when the catalogue no longer holds it — so an edit form opened on such a
 * document shows its value (the API accepts it unchanged) instead of a blank.
 */
export function documentTypeOptions(
  types: readonly DocumentTypeEntry[],
  current?: string | null,
): Array<{ value: string; label: string }> {
  const options = types.map((t) => ({ value: t.name, label: t.label }));
  if (current && !types.some((t) => t.name === current)) options.push({ value: current, label: current });
  return options;
}

/** Every catalogue reader refetches — call after a type is created or deleted. */
export function invalidateDocumentTypes(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['library', 'document-types'] });
}

/**
 * The key a new type is stored under, from the label a person typed — the
 * API's alphabet (lowercase letters, digits, underscores). Empty when the
 * label holds none of them.
 */
export function documentTypeKeyFor(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/** Create a tenant custom type (write:library.documents). */
export async function createDocumentType(input: { name: string; label: string }): Promise<void> {
  await apiClient.post(DOCUMENT_TYPE_CATALOGUE_PATH, input);
}

/** Delete a tenant custom type (write:library.documents). A type in use answers 409 IN_USE. */
export async function deleteDocumentType(id: string): Promise<void> {
  await apiClient.delete(`${DOCUMENT_TYPE_CATALOGUE_PATH}/${id}`);
}
